import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, type SxProps } from '@mui/material';

type UseResizeReturn = {
  resizeBar: JsxNode;
  width: number;
};

type Props = {
  defaultWidth: number;
  position: 'right' | 'left';
};

const COMMON_SX: SxProps = {
  zIndex: 'fab',
  display: { xs: 'none', md: 'block' },
  height: '100%',
  cursor: 'col-resize',
};

export default function useResizeBar({ defaultWidth, position }: Props): UseResizeReturn {
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [width, setWidth] = useState(defaultWidth);

  const enableResize = useCallback(() => {
    setResizeEnabled(true);
  }, [setResizeEnabled]);

  const disableResize = useCallback(() => {
    setResizeEnabled(false);
  }, [setResizeEnabled]);

  const resize = useCallback(
    (e: MouseEvent) => {
      if (resizeEnabled) {
        setWidth(getWidth(position, e.clientX));
      }
    },
    [resizeEnabled, setWidth, position]
  );

  useEffect(() => {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', disableResize);
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', disableResize);
    };
  }, [disableResize, resize]);

  const sx = useMemo(() => {
    switch (position) {
      case 'right':
        return {
          ...COMMON_SX,
          marginRight: '-10px',
          borderLeft: '4px solid transparent',
          borderRight: '6px solid transparent',
          borderLeftColor: resizeEnabled ? 'rgba(0,0,0, 0.3)' : 'transparent',
          '&:hover': { borderLeftColor: 'rgba(0,0,0, 0.2)' },
        };
      case 'left':
        return {
          ...COMMON_SX,
          marginLeft: '-10px',
          borderRight: '4px solid transparent',
          borderRightColor: resizeEnabled ? 'rgba(0,0,0, 0.3)' : 'transparent',
          borderLeft: '6px solid transparent',
          '&:hover': { borderRightColor: 'rgba(0,0,0, 0.2)' },
        };
    }
  }, [position, resizeEnabled]);

  return {
    width,
    resizeBar: (
      <Box
        sx={sx}
        onMouseDown={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          enableResize();
        }}
      />
    ),
  };
}

function getWidth(position: 'left' | 'right', clientX: number) {
  switch (position) {
    case 'right':
      return window.innerWidth - clientX;
    case 'left':
      return clientX;
  }
}
